import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { CmsProvider } from "@/context/CmsContext";
import CmsEditToggle from "@/components/CmsEditToggle";
import ScrollToTop from "@/components/ScrollToTop";
import "@/App.css";

// --- Public pages (eager — needed on the most-trafficked landing pages)
import Landing from "@/pages/Landing";
import Signup from "@/pages/Signup";
import PublicBooking from "@/pages/PublicBooking";
import Services from "@/pages/Services";
import ServiceDetail from "@/pages/ServiceDetail";
import CoursesChooser from "@/pages/CoursesChooser";
import ServiceAreas from "@/pages/ServiceAreas";
import SuburbPage from "@/pages/SuburbPage";
import About from "@/pages/About";
import Contact from "@/pages/Contact";
import Faq from "@/pages/Faq";
import Testimonials from "@/pages/Testimonials";
import Tips from "@/pages/Tips";
import Privacy from "@/pages/Privacy";
import Terms from "@/pages/Terms";
import NonAversive from "@/pages/NonAversive";
import CoursesOnline from "@/pages/CoursesOnline";
import CoursesInPerson from "@/pages/CoursesInPerson";
import TipDetail from "@/pages/TipDetail";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import PostCheckout from "@/pages/PostCheckout";
import LearnHub from "@/pages/LearnHub";
import Team from "@/pages/Team";
import FollowUpAccept from "@/pages/FollowUpAccept";
import RescheduleAccept from "@/pages/RescheduleAccept";
import BookConsult from "@/pages/BookConsult";
import Store from "@/pages/Store";
import PackagesPublic from "@/pages/Packages";

// --- Portal pages (lazy — only logged-in clients hit these)
const PortalLayout = lazy(() => import("@/pages/portal/PortalLayout"));
const PortalProfile = lazy(() => import("@/pages/portal/Profile"));
const PortalDashboard = lazy(() => import("@/pages/portal/Dashboard"));
const PortalDogs = lazy(() => import("@/pages/portal/Dogs"));
const PortalBookings = lazy(() => import("@/pages/portal/Bookings"));
const PortalComingSoon = lazy(() => import("@/pages/portal/ComingSoon"));
const PortalMemories = lazy(() => import("@/pages/portal/Memories"));
const PortalTrainingPlans = lazy(() => import("@/pages/portal/TrainingPlans"));
const PortalTrainingPlanDetail = lazy(() => import("@/pages/portal/TrainingPlans").then((m) => ({ default: m.PortalTrainingPlanDetail })));
const PortalCourses = lazy(() => import("@/pages/portal/Courses"));
const PortalCourseDetail = lazy(() => import("@/pages/portal/CourseDetail"));
const PortalSettings = lazy(() => import("@/pages/portal/Settings"));
const PortalAgreements = lazy(() => import("@/pages/portal/Agreements"));
const PortalWaitlist = lazy(() => import("@/pages/portal/Waitlist"));
const PortalCommunications = lazy(() => import("@/pages/portal/Communications"));
const PortalTricks = lazy(() => import("@/pages/portal/Tricks"));
const PortalObedienceCues = lazy(() => import("@/pages/portal/ObedienceCues"));
const TrickCertificate = lazy(() => import("@/pages/portal/TrickCertificate"));
const ObedienceCueCertificate = lazy(() => import("@/pages/portal/ObedienceCueCertificate"));
const GeneralTipsHub = lazy(() => import("@/pages/GeneralTipsHub"));
const GeneralTipArticle = lazy(() => import("@/pages/GeneralTipArticle"));

// --- Super-admin (platform ops) — Phase 5H
const SuperLogin = lazy(() => import("@/pages/super/Login"));
const SuperLayout = lazy(() => import("@/pages/super/Layout"));
const SuperDashboard = lazy(() => import("@/pages/super/Dashboard"));
const SuperTenants = lazy(() => import("@/pages/super/Tenants"));
const SuperAudit = lazy(() => import("@/pages/super/Audit"));
const SuperBugReports = lazy(() => import("@/pages/super/BugReports"));
const SuperFeatureRequests = lazy(() => import("@/pages/super/FeatureRequests"));
const SuperWalkthroughFeedback = lazy(() => import("@/pages/super/WalkthroughFeedback"));

// --- Onboarding wizard — Phase 5I
const GetStarted = lazy(() => import("@/pages/GetStarted"));

// --- Admin pages (lazy — only the team / staff hit these)
const AdminLayout = lazy(() => import("@/pages/admin/AdminLayout"));
const AdminDashboard = lazy(() => import("@/pages/admin/Dashboard"));
const AdminClients = lazy(() => import("@/pages/admin/Clients"));
const AdminDogs = lazy(() => import("@/pages/admin/Dogs"));
const AdminDogProfile = lazy(() => import("@/pages/admin/DogProfile"));
const AdminInfoRequests = lazy(() => import("@/pages/admin/InfoRequests"));
const AdminClientProfile = lazy(() => import("@/pages/admin/ClientProfile"));
const AdminTags = lazy(() => import("@/pages/admin/Tags"));
const AdminServices = lazy(() => import("@/pages/admin/Services"));
const AdminSuperCategories = lazy(() => import("@/pages/admin/SuperCategories"));
const AdminBookings = lazy(() => import("@/pages/admin/Bookings"));
const AdminCourses = lazy(() => import("@/pages/admin/Courses"));
const AdminCoursesBrowse = lazy(() => import("@/pages/admin/CoursesBrowse"));
const AdminMediaLibrary = lazy(() => import("@/pages/admin/MediaLibrary"));
const AdminCourseEditor = lazy(() => import("@/pages/admin/CourseEditor"));
const AdminLeads = lazy(() => import("@/pages/admin/Leads"));
const AdminClasses = lazy(() => import("@/pages/admin/Classes"));
const AdminLocations = lazy(() => import("@/pages/admin/Locations"));
const AdminBlog = lazy(() => import("@/pages/admin/BlogAdmin"));
const AdminNotifications = lazy(() => import("@/pages/admin/Notifications"));
const AdminPageEditor = lazy(() => import("@/pages/admin/PageEditor"));
const AdminIntakeForms = lazy(() => import("@/pages/admin/IntakeForms"));
const AdminTimeOff = lazy(() => import("@/pages/admin/TimeOff"));
const AdminBusinessClosures = lazy(() => import("@/pages/admin/BusinessClosures"));
const AdminAvailability = lazy(() => import("@/pages/admin/Availability"));
const AdminDiscoveryCalls = lazy(() => import("@/pages/admin/DiscoveryCalls"));
const AdminProducts = lazy(() => import("@/pages/admin/Products"));
const AdminPackages = lazy(() => import("@/pages/admin/Packages"));
const AdminReports = lazy(() => import("@/pages/admin/Reports"));
const AdminCms = lazy(() => import("@/pages/admin/Cms"));
const AdminPayments = lazy(() => import("@/pages/admin/Payments"));
const AdminStripeSettings = lazy(() => import("@/pages/admin/StripeSettings"));
const AdminCoupons = lazy(() => import("@/pages/admin/Coupons"));
const AdminTasks = lazy(() => import("@/pages/admin/Tasks"));
const AdminWorkHours = lazy(() => import("@/pages/admin/WorkHours"));
const AdminTaskSettings = lazy(() => import("@/pages/admin/TaskSettings"));
const AdminStaff = lazy(() => import("@/pages/admin/Staff"));
const AdminStaffProfile = lazy(() => import("@/pages/admin/StaffProfile"));
const AdminAccount = lazy(() => import("@/pages/admin/Account"));
const HelpCentre = lazy(() => import("@/pages/admin/HelpCentre"));
const AdminBookingRequests = lazy(() => import("@/pages/admin/BookingRequests"));
const AdminActivityLog = lazy(() => import("@/pages/admin/ActivityLog"));
const AdminFinance = lazy(() => import("@/pages/admin/Finance"));
const AdminEmbeds = lazy(() => import("@/pages/admin/Embeds"));
const AdminOrgSettings = lazy(() => import("@/pages/admin/OrgSettings"));
const AdminBillingSettings = lazy(() => import("@/pages/admin/BillingSettings"));
const AdminSubscriptionSettings = lazy(() => import("@/pages/admin/SubscriptionSettings"));
const AdminAgreements = lazy(() => import("@/pages/admin/Agreements"));
const AdminAgreementEditor = lazy(() => import("@/pages/admin/AgreementEditor"));
const AdminImportCentre = lazy(() => import("@/pages/admin/ImportCentre"));
const AdminInvoices = lazy(() => import("@/pages/admin/Invoices"));
const AdminInvoiceDetail = lazy(() => import("@/pages/admin/InvoiceDetail"));
const AdminClientInvoices = lazy(() => import("@/pages/admin/ClientInvoices"));
const AdminData = lazy(() => import("@/pages/admin/Data"));
const AdminCalendar = lazy(() => import("@/pages/admin/Calendar"));
const AdminManuals = lazy(() => import("@/pages/admin/Manuals"));
const AdminWorkflows = lazy(() => import("@/pages/admin/Workflows"));
const AdminWorkflowEditor = lazy(() => import("@/pages/admin/WorkflowEditor"));
const AdminInbox = lazy(() => import("@/pages/admin/Inbox"));
const AdminMarketing = lazy(() => import("@/pages/admin/Marketing"));
const AdminEmails = lazy(() => import("@/pages/admin/Emails"));
const AdminEmailTriggers = lazy(() => import("@/pages/admin/EmailTriggers"));
const AdminTrickTitles = lazy(() => import("@/pages/admin/TrickTitles"));
const AdminTrickPending = lazy(() => import("@/pages/admin/TrickPending"));
const AdminObedienceCues = lazy(() => import("@/pages/admin/ObedienceCues"));
const AdminObedienceCuesPending = lazy(() => import("@/pages/admin/ObedienceCuesPending"));
const AdminTrainingPlans = lazy(() => import("@/pages/admin/TrainingPlans"));
const AdminTrainingPlanEdit = lazy(() => import("@/pages/admin/TrainingPlanEdit"));
const AdminChatWidget = lazy(() => import("@/pages/admin/ChatWidgetAdmin"));
const AdminSmartAI = lazy(() => import("@/pages/admin/SmartAI"));
const AdminSmartAILibrary = lazy(() => import("@/pages/admin/SmartAILibrary"));
const AdminReportDrafts = lazy(() => import("@/pages/admin/ReportDrafts"));
const AdminPaymentsDiagnostics = lazy(() => import("@/pages/admin/PaymentsDiagnostics"));
const AdminAnnouncements = lazy(() => import("@/pages/admin/Announcements"));
const AdminAssistant = lazy(() => import("@/pages/admin/Assistant"));
const AdminScheduling = lazy(() => import("@/pages/admin/Scheduling"));
const AdminCustomTags = lazy(() => import("@/pages/admin/CustomMergeTags"));
const AdminWelcomePacks = lazy(() => import("@/pages/admin/WelcomePacks"));
const AdminImapHelp = lazy(() => import("@/pages/admin/ImapHelp"));
const AdminCoach = lazy(() => import("@/pages/admin/Coach"));
const AdminSeo = lazy(() => import("@/pages/admin/Seo"));
const Welcome = lazy(() => import("@/pages/Welcome"));
const ForgotPassword = lazy(() => import("@/pages/ForgotPassword"));
const ResetPassword = lazy(() => import("@/pages/ResetPassword"));
const AdminRecovery = lazy(() => import("@/pages/AdminRecovery"));

function RouteFallback() {
  return <div className="p-10 text-stone-500 text-sm">Loading…</div>;
}

function Protected({ children, roles }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="p-10 text-stone-500">Loading...</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/" replace />;
  return children;
}

function App() {
  return (
    <AuthProvider>
      <CmsProvider>
        <BrowserRouter>
          <ScrollToTop />
          <CmsEditToggle />
          <Suspense fallback={<RouteFallback />}>
          <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/get-started" element={<GetStarted />} />
          <Route path="/book/:slug" element={<PublicBooking />} />
          <Route path="/signup" element={<Signup />} />
          <Route path="/services" element={<Services />} />
          <Route path="/services/:slug" element={<ServiceDetail />} />
          <Route path="/follow-up/:token" element={<FollowUpAccept />} />
          <Route path="/reschedule/:token" element={<RescheduleAccept />} />
          <Route path="/book-consult" element={<BookConsult />} />
          <Route path="/service-areas" element={<ServiceAreas />} />
          <Route path="/service-areas/:suburb" element={<SuburbPage />} />
          <Route path="/about" element={<About />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/faq" element={<Faq />} />
          <Route path="/testimonials" element={<Testimonials />} />
          <Route path="/team" element={<Team />} />
          <Route path="/tips" element={<Tips />} />
          <Route path="/tips/:slug" element={<TipDetail />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/non-aversive-policy" element={<NonAversive />} />
          <Route path="/courses" element={<CoursesChooser />} />
          <Route path="/courses/online" element={<CoursesOnline />} />
          <Route path="/courses/in-person" element={<CoursesInPerson />} />
          <Route path="/general-tips" element={<GeneralTipsHub />} />
          <Route path="/general-tips/:slug" element={<GeneralTipArticle />} />
          <Route path="/store" element={<Store />} />
          <Route path="/packages" element={<PackagesPublic />} />
          <Route path="/learn/:slug" element={<LearnHub />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/post-checkout" element={<PostCheckout />} />
          <Route path="/welcome/:token" element={<Welcome />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/admin-recovery" element={<AdminRecovery />} />

          <Route path="/portal" element={<Protected roles={["client", "admin", "trainer"]}><PortalLayout /></Protected>}>
            <Route index element={<PortalDashboard />} />
            <Route path="dogs" element={<PortalDogs />} />
            <Route path="bookings" element={<PortalBookings />} />
            <Route path="profile" element={<PortalProfile />} />
            <Route path="reports" element={<PortalComingSoon />} />
            <Route path="courses" element={<PortalCourses />} />
            <Route path="courses/:id" element={<PortalCourseDetail />} />
            <Route path="memories" element={<PortalMemories />} />
            <Route path="training" element={<PortalTrainingPlans />} />
            <Route path="training/:planId" element={<PortalTrainingPlanDetail />} />
            <Route path="coach" element={<PortalComingSoon />} />
            <Route path="tricks" element={<PortalTricks />} />
            <Route path="tricks/certificate/:dogId/:titleId" element={<TrickCertificate />} />
            <Route path="obedience-cues" element={<PortalObedienceCues />} />
            <Route path="obedience-cues/certificate/:dogId/:titleId" element={<ObedienceCueCertificate />} />
            <Route path="settings" element={<PortalSettings />} />
            <Route path="agreements" element={<PortalAgreements />} />
            <Route path="waitlist" element={<PortalWaitlist />} />
            <Route path="communications" element={<PortalCommunications />} />
          </Route>

          <Route path="/admin" element={<Protected roles={["admin", "trainer"]}><AdminLayout /></Protected>}>
            <Route index element={<AdminDashboard />} />
            <Route path="clients" element={<AdminClients />} />
            <Route path="dogs" element={<AdminDogs />} />
            <Route path="dogs/:dogId" element={<AdminDogProfile />} />
            <Route path="info-requests" element={<AdminInfoRequests />} />
            <Route path="clients/:id" element={<AdminClientProfile />} />
            <Route path="bookings" element={<AdminBookings />} />
            <Route path="courses" element={<AdminCourses />} />
            <Route path="courses-browse" element={<AdminCoursesBrowse />} />
            <Route path="media" element={<AdminMediaLibrary />} />
            <Route path="charlottes-brain" element={<Navigate to="/admin/manuals" replace />} />
            <Route path="courses/:id" element={<AdminCourseEditor />} />
            <Route path="leads" element={<AdminLeads />} />
            <Route path="classes" element={<AdminClasses />} />
            <Route path="locations" element={<AdminLocations />} />
            <Route path="blog" element={<AdminBlog />} />
            <Route path="notifications" element={<AdminNotifications />} />
            <Route path="pages" element={<AdminPageEditor />} />
            <Route path="intake-forms" element={<AdminIntakeForms />} />
            <Route path="time-off" element={<AdminTimeOff />} />
            <Route path="business-closures" element={<AdminBusinessClosures />} />
            <Route path="availability" element={<AdminAvailability />} />
            <Route path="discovery-calls" element={<AdminDiscoveryCalls />} />
            <Route path="products" element={<AdminProducts />} />
            <Route path="packages" element={<AdminPackages />} />
            <Route path="reports" element={<AdminReports />} />
            <Route path="tags" element={<AdminTags />} />
            <Route path="services" element={<AdminServices />} />
            <Route path="super-categories" element={<AdminSuperCategories />} />
            <Route path="cms" element={<AdminCms />} />
            <Route path="payments" element={<AdminPayments />} />
            <Route path="payments/diagnostics" element={<AdminPaymentsDiagnostics />} />
            <Route path="stripe" element={<AdminStripeSettings />} />
            <Route path="coupons" element={<AdminCoupons />} />
            <Route path="tasks" element={<AdminTasks />} />
            <Route path="work-hours" element={<AdminWorkHours />} />
            <Route path="task-settings" element={<AdminTaskSettings />} />
            <Route path="staff" element={<AdminStaff />} />
            <Route path="staff/:id" element={<AdminStaffProfile />} />
            <Route path="account" element={<AdminAccount />} />
            <Route path="help" element={<HelpCentre />} />
            <Route path="booking-requests" element={<AdminBookingRequests />} />
            <Route path="activity" element={<AdminActivityLog />} />
            <Route path="finance" element={<AdminFinance />} />
            <Route path="embeds" element={<AdminEmbeds />} />
            <Route path="settings/organisation" element={<AdminOrgSettings />} />
            <Route path="settings/billing" element={<AdminBillingSettings />} />
            <Route path="settings/subscription" element={<AdminSubscriptionSettings />} />
            <Route path="settings/agreements" element={<AdminAgreements />} />
            <Route path="settings/agreements/:id" element={<AdminAgreementEditor />} />
            <Route path="import" element={<AdminImportCentre />} />
            <Route path="invoices" element={<AdminInvoices />} />
            <Route path="invoices/:id" element={<AdminInvoiceDetail />} />
            <Route path="clients/:id/invoices" element={<AdminClientInvoices />} />
            <Route path="data" element={<AdminData />} />
            <Route path="calendar" element={<AdminCalendar />} />
            <Route path="manuals" element={<AdminManuals />} />
            <Route path="workflows" element={<AdminWorkflows />} />
            <Route path="workflows/:id" element={<AdminWorkflowEditor />} />
            <Route path="inbox" element={<AdminInbox />} />
            <Route path="marketing" element={<AdminMarketing />} />
            <Route path="emails" element={<AdminEmails />} />
            <Route path="email-triggers" element={<AdminEmailTriggers />} />
            <Route path="trick-titles" element={<AdminTrickTitles />} />
            <Route path="trick-titles/pending" element={<AdminTrickPending />} />
            <Route path="trick-titles/certificate/:dogId/:titleId" element={<TrickCertificate />} />
            <Route path="obedience-cues" element={<AdminObedienceCues />} />
            <Route path="training-plans" element={<AdminTrainingPlans />} />
            <Route path="training-plans/templates/:id" element={<AdminTrainingPlanEdit mode="template" />} />
            <Route path="training-plans/:id" element={<AdminTrainingPlanEdit mode="plan" />} />
            <Route path="chat-widget" element={<AdminChatWidget />} />
            <Route path="smartai" element={<AdminSmartAI />} />
            <Route path="smartai/:transcriptId" element={<AdminSmartAI />} />
            <Route path="smartai-library" element={<AdminSmartAILibrary />} />
            <Route path="report-drafts" element={<AdminReportDrafts />} />
            <Route path="announcements" element={<AdminAnnouncements />} />
            <Route path="assistant" element={<AdminAssistant />} />
            <Route path="assistant/:runId" element={<AdminAssistant />} />
            <Route path="obedience-cues/pending" element={<AdminObedienceCuesPending />} />
            <Route path="obedience-cues/certificate/:dogId/:titleId" element={<ObedienceCueCertificate />} />
            <Route path="scheduling" element={<AdminScheduling />} />
            <Route path="emails/custom-tags" element={<AdminCustomTags />} />
            <Route path="clients/welcome-packs" element={<AdminWelcomePacks />} />
            <Route path="help/imap" element={<AdminImapHelp />} />
            <Route path="coach" element={<AdminCoach />} />
            <Route path="seo" element={<AdminSeo />} />
          </Route>

          {/* Super-admin (platform ops) */}
          <Route path="/super/login" element={<SuperLogin />} />
          <Route path="/super" element={<SuperLayout />}>
            <Route index element={<SuperDashboard />} />
            <Route path="tenants" element={<SuperTenants />} />
            <Route path="bug-reports" element={<SuperBugReports />} />
            <Route path="feature-requests" element={<SuperFeatureRequests />} />
            <Route path="walkthrough-feedback" element={<SuperWalkthroughFeedback />} />
            <Route path="audit" element={<SuperAudit />} />
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </Suspense>
      </BrowserRouter>
      <Toaster position="bottom-right" richColors />
      </CmsProvider>
    </AuthProvider>
  );
}

export default App;
